import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { auditTime, filter, Subject, takeUntil } from 'rxjs';
import { CreateObjectiveComponent, PopupProjectData } from '../create-project/create-project.component';
import { ApiService } from '../../services/api.service';
import { CommonServices } from '../../services/common.services';
import { ProjectService } from '../../services/project.service';
import { ToasterService } from '../../services/toaster.service';
import { IconService } from '../../services/svg-icon.service';
import { ViewModelMapperService } from '../../services/view-model.mapper.service';
import { ExperimentVM } from '../../models/view-models/vm-experiment.model';
import { ProjectSortKey, ProjectSortOption, ProjectVM, vm_objective } from '../../models/view-models/vm-projects.model';
import { vm_tag } from '../../models/view-models/vm-tags.model';
import { vm_user } from '../../models/view-models/vm_user.model';
import { RelatedExperimentsAccordionComponent } from '../../ui-component-library/related-experiments-accordion/related-experiments-accordion.component';
import { ProjectInfoCardComponent } from '../../ui-component-library/project-info-card/project-info-card.component';
import {
  ProductChipData,
  ProductInfoCardComponent,
} from '../../ui-component-library/product-info-card/product-info-card.component';
import { ProjectSearchComponent } from '../../ui-component-library/project-search/project-search.component';
import { LoaderService } from '../../services/loader.service';
import { DateTimeService } from '../../services/date-time.service';
import { MobileProjectsStateService } from '../../services/mobile-projects-state.service';
import { MobileProjectsComponent } from './mobile-projects/mobile-projects.component';

// Page view states - drives what the template renders
export type ProjectPageViewState = 'loading' | 'ready' | 'empty' | 'error';

@Component({
  selector: 'app-campbell-projects',
  imports: [
    MatCardModule,
    MatIconModule,
    MatFormFieldModule,
    MatListModule,
    FormsModule,
    CommonModule,
    MatChipsModule,
    MatToolbarModule,
    MatTooltipModule,
    ProjectSearchComponent,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatDialogModule,
    MatNativeDateModule,
    MatDatepickerModule,
    ProductInfoCardComponent,
    ProjectInfoCardComponent,
    RelatedExperimentsAccordionComponent,
    MobileProjectsComponent,
  ],
  templateUrl: './campbell-projects.html',
  styleUrls: ['./campbell-projects.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.Emulated,
})
export class CampbellProjects implements OnInit, OnDestroy {
  // ─── View State ───────────────────────────────────────────────────────────────
  // 'loading' → spinner shown, table hidden
  // 'ready'   → table shown with data
  // 'empty'   → table shown, matNoDataRow shown (true empty dataset, not a flicker)
  // 'error'   → error message shown
  viewState: ProjectPageViewState = 'loading';

  // Keep loading as computed getter so existing *ngIf="!loading" in template still works
  get loading(): boolean {
    return this.viewState === 'loading';
  }

  // ─── Data ─────────────────────────────────────────────────────────────────────
  projects: ProjectVM[] = [];
  filteredProjectList: ProjectVM[] = [];

  displayedColumns: string[] = ['project', 'products', 'experiments'];
  dataSource = new MatTableDataSource<ProjectVM>([]);
  searchTerm: string = '';
  filterRange: number = 0;
  errorMessage: string = '';

  sortBy: ProjectSortKey = 'modifiedDate';
  readonly projectSortOptions: ProjectSortOption[] = [
    { key: 'modifiedDate', label: 'Modified date' },
    { key: 'startDateAsc', label: 'Start date (Old - New)' },
    { key: 'startDateDesc', label: 'Start date (New - Old)' },
  ];

  // ─── Private State ────────────────────────────────────────────────────────────
  private destroy$ = new Subject<void>();
  private isLoadInProgress = false;
  private hasFirstLoadCompleted = false;
  // Tracks whether we have received at least one dataLoaded event
  private dataLoadedEventReceived = false;

  // Derived data maps (per project)
  private productsByProject = new Map<string, ProductChipData[]>();
  private developersByProject = new Map<string, vm_user[]>();
  private experimentsByProject = new Map<string, Partial<ExperimentVM>[]>();
  private experimentTagsByProject = new Map<string, vm_tag[]>();

  // Per-project chip selection state
  private selectedProductIdsByProject = new Map<string, Set<string>>();
  private selectedDeveloperIdsByProject = new Map<string, Set<string>>();
  private selectedTagIdsByProject = new Map<string, Set<string>>();

  private readonly LOADER_OP_ID = 'campbell-projects-load';
  private static hasLoadedOnceInRuntime = false;
  private hydrationRetryTimer: ReturnType<typeof setTimeout> | null = null;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('projectsPageBody') projectsPageBody?: ElementRef<HTMLElement>;

  private pendingProjectIdToFocus: string | null = null;

  constructor(
    private iconService: IconService,
    private cdr: ChangeDetectorRef,
    private pmSrvc: ProjectService,
    private toastSrvc: ToasterService,
    private apiService: ApiService,
    private vmMapper: ViewModelMapperService,
    private dialog: MatDialog,
    private dateTimeService: DateTimeService,
    private commonSrvc: CommonServices,
    private router: Router,
    private loaderService: LoaderService,
    private mobileProjectsStateService: MobileProjectsStateService
  ) {
    this.apiService.dataChangeEvent$
      .pipe(
        filter((data: any) => !!data?.dataLoaded),
        auditTime(200),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.dataLoadedEventReceived = true;
        this.loadInitialData();
      });
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.apiService.isMobileDevice) {
      this.searchTerm = this.mobileProjectsStateService.searchTerm;
    }
    this.loaderService.show(this.LOADER_OP_ID);
    this.viewState = 'loading';

    if (this.hasWarmCacheData()) {
      this.dataLoadedEventReceived = true;
    }

    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.loaderService.hide(this.LOADER_OP_ID);
    if (this.hydrationRetryTimer) {
      clearTimeout(this.hydrationRetryTimer);
      this.hydrationRetryTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Data Load ───────────────────────────────────────────────────────────────

  loadInitialData(): void {
    if (!this.dataLoadedEventReceived) {
      // If event was missed, still allow hydration when cache is actually warm.
      if (this.hasWarmCacheData()) {
        this.dataLoadedEventReceived = true;
      } else {
        this.viewState = 'loading';
        this.loaderService.show(this.LOADER_OP_ID);
        this.scheduleHydrationRetry();
        return;
      }
    }

    if (this.hydrationRetryTimer) {
      clearTimeout(this.hydrationRetryTimer);
      this.hydrationRetryTimer = null;
    }

    if (this.isLoadInProgress) return;
    this.isLoadInProgress = true;

    try {
      this.projects = this.vmMapper.mapProjectData(
        this.apiService.projects ?? [],
        this.apiService.experiments ?? [],
        this.apiService.tags ?? [],
        this.apiService.tagApplications ?? [],
        this.apiService.categories ?? [],
        this.apiService.categoryApplications ?? [],
        this.apiService.icons ?? [],
        this.apiService.users ?? [],
        this.apiService.batches ?? [],
        this.apiService.objectives ?? [],
        this.apiService.objectiveApplications ?? [],
        this.apiService.projectTypes ?? [],
        this.apiService.statuses ?? [],
        this.apiService.products ?? [],
        this.apiService.productProjects ?? []
      );

      this.filteredProjectList = this.applyProjectSort([...this.projects]);
      this.rebuildDerivedMaps(this.projects);
      this.dataSource.data = this.applyProjectSort([...this.filteredProjectList]);

      this.hasFirstLoadCompleted = true;
      CampbellProjects.hasLoadedOnceInRuntime = true;
      this.errorMessage = '';
      this.viewState = this.dataSource.data.length > 0 ? 'ready' : 'empty';
      if (this.apiService.isMobileDevice && this.searchTerm.trim()) {
        this.applyFilters();
      }
    } catch (err: any) {
      this.errorMessage = err?.message || 'Failed to load projects. Please try again.';
      this.viewState = 'error';
    } finally {
      this.isLoadInProgress = false;
      if (this.viewState !== 'loading') {
        this.loaderService.hide(this.LOADER_OP_ID);
      }
      this.cdr.detectChanges();
    }
  }

  private scheduleHydrationRetry(): void {
    if (this.hydrationRetryTimer) return;
    this.hydrationRetryTimer = setTimeout(() => {
      this.hydrationRetryTimer = null;
      if (!this.hasFirstLoadCompleted) {
        this.loadInitialData();
      }
    }, 400);
  }

  private hasWarmCacheData(): boolean {
    const warmCandidates = [
      this.apiService.projects,
      this.apiService.experiments,
      this.apiService.users,
      this.apiService.statuses,
      this.apiService.projectTypes,
      this.apiService.products,
      this.apiService.tags,
      this.apiService.categories,
    ];

    return warmCandidates.some((arr) => Array.isArray(arr) && arr.length > 0);
  }

  // ─── Filters ─────────────────────────────────────────────────────────────────

  onProjectSearchStr(val: string): void {
    this.searchTerm = val;
    if (this.apiService.isMobileDevice) {
      this.mobileProjectsStateService.searchTerm = val;
    }
    this.applyFilters();
  }

  onDateRangeChange(days: number): void {
    this.filterRange = days;
    this.applyFilters();
  }

  onSortOptionChange(sortKey: ProjectSortKey): void {
    this.sortBy = sortKey;
    this.applyFilters();
    this.scrollPageBodyToTop();
  }

  applyFilters(): void {
    // Do not apply filters until first load completes
    if (!this.hasFirstLoadCompleted) return;

    const now = new Date();
    const days = this.filterRange;
    const searchTerm = (this.searchTerm || '').toLowerCase();

    this.filteredProjectList = this.projects.filter((p: ProjectVM) => {
      let passesDateFilter = true;
      if (days !== 0) {
        const projectDate = p.project_start_date ? new Date(p.project_start_date) : null;
        if (projectDate) {
          const diffDays = (now.getTime() - projectDate.getTime()) / (1000 * 3600 * 24);
          passesDateFilter = diffDays >= 0 && diffDays <= days;
        }
      }

      if (!searchTerm) return passesDateFilter;

      let projectStartDate = '';
      if (p.project_start_date) {
        projectStartDate = this.dateTimeService.formatCalendarDate(p.project_start_date, 'MM/dd/yyyy').toLowerCase();
      }

      const inProject = this.apiService.isMobileDevice
        ? (p.project_title || '').toLowerCase().includes(searchTerm)
        : (p.project_title || '').toLowerCase().includes(searchTerm) ||
          (p.project_description || '').toLowerCase().includes(searchTerm) ||
          (p.project_tags || []).some((tag: vm_tag) => (tag.tagTitle || '').toLowerCase().includes(searchTerm)) ||
          (p.project_objectives || []).some((obj: vm_objective) =>
            (obj.objectiveTitle || '').toLowerCase().includes(searchTerm)
          ) ||
          (p.project_status?.appStatusTitle || '').toLowerCase().includes(searchTerm) ||
          (p.project_type?.projectTypeTitle || '').toLowerCase().includes(searchTerm) ||
          ((p.project_products || []) as any[]).some(
            (prod: any) =>
              String(prod?.productCode ?? '')
                .toLowerCase()
                .includes(searchTerm) ||
              String(prod?.productTitle ?? '')
                .toLowerCase()
                .includes(searchTerm)
          ) ||
          projectStartDate.includes(searchTerm);

      const inExperiments = (p.project_experiments || []).some((exp: Partial<ExperimentVM>) => {
        if ((exp.experimentTitle || '').toLowerCase().includes(searchTerm)) return true;
        if (this.apiService.isMobileDevice) {
          return (exp.experimentBatches || []).some(
            (batch) =>
              String(batch?.batchVersion ?? '')
                .toLowerCase()
                .includes(searchTerm) ||
              String(batch?.batchTitle ?? '')
                .toLowerCase()
                .includes(searchTerm) ||
              (batch?.childBatch || []).some(
                (child) =>
                  String(child?.batchVersion ?? '')
                    .toLowerCase()
                    .includes(searchTerm) ||
                  String(child?.batchTitle ?? '')
                    .toLowerCase()
                    .includes(searchTerm)
              )
          );
        }
        if (exp.experimentUser?.userName?.toLowerCase().includes(searchTerm)) return true;
        return (exp.experimentTags || []).some((tag: any) => (tag.tagTitle || '').toLowerCase().includes(searchTerm));
      });

      return passesDateFilter && (inProject || inExperiments);
    });

    this.filteredProjectList = this.applyProjectSort(this.filteredProjectList);
    this.dataSource.data = [...this.filteredProjectList];

    // IMPORTANT: do not flip ready <-> empty for client-side search/filter
    // empty should represent "no data in system", not "no match for current search"
    if (this.projects.length === 0) {
      this.viewState = 'empty';
    } else {
      this.viewState = 'ready';
    }

    const visibleProjectIds = new Set(
      this.filteredProjectList.map((p) => String(p?.id_project ?? '').trim()).filter(Boolean)
    );

    if (visibleProjectIds.size > 0) {
      this.cleanupSelectionMap(this.selectedProductIdsByProject, visibleProjectIds);
      this.cleanupSelectionMap(this.selectedDeveloperIdsByProject, visibleProjectIds);
      this.cleanupSelectionMap(this.selectedTagIdsByProject, visibleProjectIds);
    }
  }

  // ─── Chip Toggle Handlers ─────────────────────────────────────────────────────

  onProjectProductToggle(project: ProjectVM, productId: string): void {
    this.toggleInSelectionMap(this.selectedProductIdsByProject, project, productId);
  }

  onProjectDeveloperToggle(project: ProjectVM, developerId: string): void {
    this.toggleInSelectionMap(this.selectedDeveloperIdsByProject, project, developerId);
  }

  onProjectTagToggle(project: ProjectVM, tagId: string): void {
    this.toggleInSelectionMap(this.selectedTagIdsByProject, project, tagId);
  }

  // ─── Selection Getters ────────────────────────────────────────────────────────

  getSelectedProductIds(project: ProjectVM): Set<string> {
    return this.getOrCreateSelectionSet(this.selectedProductIdsByProject, project);
  }

  getSelectedDeveloperIds(project: ProjectVM): Set<string> {
    return this.getOrCreateSelectionSet(this.selectedDeveloperIdsByProject, project);
  }

  getSelectedTagIds(project: ProjectVM): Set<string> {
    return this.getOrCreateSelectionSet(this.selectedTagIdsByProject, project);
  }

  // ─── Derived Data Getters ─────────────────────────────────────────────────────

  getProjectProducts(project: ProjectVM): ProductChipData[] {
    return this.productsByProject.get(String(project?.id_project ?? '')) ?? [];
  }

  getProjectExperimentDevelopers(project: ProjectVM): vm_user[] {
    return this.developersByProject.get(String(project?.id_project ?? '')) ?? [];
  }

  getProjectExperimentTags(project: ProjectVM): vm_tag[] {
    return this.experimentTagsByProject.get(String(project?.id_project ?? '')) ?? [];
  }

  getProjectExperiments(project: ProjectVM): Partial<ExperimentVM>[] {
    return this.experimentsByProject.get(String(project?.id_project ?? '')) ?? [];
  }

  // ─── Filtered Experiments (for accordion) ────────────────────────────────────

  getFilteredProjectExperiments(project: ProjectVM): Partial<ExperimentVM>[] {
    const all = this.getProjectExperiments(project);
    const selectedProducts = this.getSelectedProductIds(project);
    const selectedDevelopers = this.getSelectedDeveloperIds(project);
    const selectedTags = this.getSelectedTagIds(project);

    if (!selectedProducts.size && !selectedDevelopers.size && !selectedTags.size) {
      return all;
    }

    return all.filter((exp) => this.matchesExperimentFilters(exp, selectedProducts, selectedDevelopers, selectedTags));
  }

  // ─── Misc ─────────────────────────────────────────────────────────────────────

  getProjectTags(project: ProjectVM): vm_tag[] {
    return project.project_tags;
  }

  highlightDate(date: string | Date, search: string): string {
    return this.commonSrvc.highlightFormattedInstant(date, search);
  }

  showToastMessage(data: any): void {
    if (data) {
      if (data.success) {
        this.toastSrvc.success(data.message || 'Operation successful!');
      } else {
        this.toastSrvc.error(data.message || 'Operation failed.');
      }
    }
  }

  createProject(): void {
    const existing = this.dialog.getDialogById('create-project-dialog');
    if (existing) {
      existing.componentInstance.focus();
      return;
    }
    this.dialog
      .open(CreateObjectiveComponent, {
        id: 'create-project-dialog',
        width: '800px',
        height: '82vh',
        autoFocus: false,
        restoreFocus: false,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.showToastMessage(result);
          this.pendingProjectIdToFocus = String(result?.projectId ?? '').trim() || null;
          this.loadInitialData();

          if (result?.success) {
            this.focusPendingProjectOrTop();
          }
        }
      });
  }

  editProject(project: ProjectVM): void {
    const existing = this.dialog.getDialogById('edit-create-project-dialog');
    if (existing) {
      existing.componentInstance.focus();
      return;
    }
    const projectData = this.projects.find((p) => p.id_project === project.id_project);
    if (projectData) {
      this.pmSrvc.setProjectForEdit(projectData);
    }
    this.dialog
      .open(CreateObjectiveComponent, {
        id: 'edit-create-project-dialog',
        width: '800px',
        height: '82vh',
        autoFocus: false,
        restoreFocus: false,
        disableClose: true,
        data: <PopupProjectData>{ projectId: project.id_project },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.showToastMessage(result);

          this.pendingProjectIdToFocus = String(project.id_project ?? '').trim() || null;
          this.loadInitialData();
          if (result?.success) {
            this.focusPendingProjectOrTop();
          }
        }
      });
  }

  iconMapper(value: string): string {
    let icon = '';
    const iconName = value?.toLowerCase();
    if (iconName) {
      if (iconName.includes('active')) icon = 'overview';
      else if (iconName.includes('hold')) icon = 'inactive_order';
      else if (iconName.includes('archive')) icon = 'archive';
    }
    return icon;
  }

  onExperimentClick(exp: any): void {
    this.pmSrvc.clearExperimentForEdit();
    this.pmSrvc.setExperimentForEdit(exp);
    this.router.navigate(['/experiments']);
  }

  trackByProjectId(index: number, row: ProjectVM): string {
    return String(row?.id_project ?? index);
  }

  // ─── Private Helpers ─────────────────────────────────────────────────────────

  private applyProjectSort(projects: ProjectVM[]): ProjectVM[] {
    const comparator = this.projectSortComparators[this.sortBy] ?? this.projectSortComparators.modifiedDate;
    return projects.sort(comparator);
  }

  private rebuildDerivedMaps(projects: ProjectVM[]): void {
    this.productsByProject.clear();
    this.developersByProject.clear();
    this.experimentsByProject.clear();
    this.experimentTagsByProject.clear();

    for (const project of projects ?? []) {
      const projectId = String(project?.id_project ?? '').trim();
      if (!projectId) continue;

      // Products (unique, sorted)
      const seenProducts = new Set<string>();
      const products: ProductChipData[] = (project?.project_products ?? [])
        .filter((p) => {
          const id = String(p?.id ?? '');
          if (!id || seenProducts.has(id)) return false;
          seenProducts.add(id);
          return true;
        })
        .map((p) => ({
          id: String(p.id),
          title: String(p.productTitle ?? ''),
          code: p.productCode ? String(p.productCode) : undefined,
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

      // Developers (unique per experiment, sorted)
      const developersById = new Map<string, vm_user>();
      for (const exp of project?.project_experiments ?? []) {
        const user = exp?.experimentUser;
        const userId = String(user?.userId ?? '').trim();
        if (!user || !userId || developersById.has(userId)) continue;
        developersById.set(userId, user);
      }
      const developers = Array.from(developersById.values()).sort((a, b) =>
        String(a.userName ?? '').localeCompare(String(b.userName ?? ''))
      );

      // Experiments (unique by id)
      const seenExperiments = new Set<string>();
      const experiments = (project?.project_experiments ?? []).filter((exp) => {
        const id = String(exp?.id ?? '');
        if (!id || seenExperiments.has(id)) return false;
        seenExperiments.add(id);
        return true;
      });

      // Experiment Tags (unique, sorted)
      const tagsByKey = new Map<string, vm_tag>();
      for (const exp of project?.project_experiments ?? []) {
        for (const tag of exp?.experimentTags ?? []) {
          const key = String((tag as any)?.id ?? tag?.tagTitle ?? '')
            .trim()
            .toLowerCase();
          if (!key || tagsByKey.has(key)) continue;
          tagsByKey.set(key, tag as vm_tag);
        }
      }
      const experimentTags = Array.from(tagsByKey.values()).sort((a, b) =>
        String(a?.tagTitle ?? '').localeCompare(String(b?.tagTitle ?? ''))
      );

      this.productsByProject.set(projectId, products);
      this.developersByProject.set(projectId, developers);
      this.experimentsByProject.set(projectId, experiments);
      this.experimentTagsByProject.set(projectId, experimentTags);
    }
  }

  private matchesExperimentFilters(
    exp: Partial<ExperimentVM>,
    selectedProducts: Set<string>,
    selectedDevelopers: Set<string>,
    selectedTags: Set<string>
  ): boolean {
    if (selectedDevelopers.size) {
      const expDevId = String(exp?.experimentUser?.userId ?? '').trim();
      if (!expDevId || !selectedDevelopers.has(expDevId)) return false;
    }

    if (selectedTags.size) {
      const expTagKeys = new Set(
        (exp?.experimentTags ?? []).map((tag) => this.getTagKey(tag as vm_tag)).filter(Boolean)
      );
      const hasTagMatch = Array.from(selectedTags).some((tagId) => expTagKeys.has(tagId));
      if (!hasTagMatch) return false;
    }

    if (selectedProducts.size) {
      const expProductIds = this.getExperimentProductIds(exp);
      const hasProductMatch = Array.from(selectedProducts).some((pid) => expProductIds.has(pid));
      if (!hasProductMatch) return false;
    }

    return true;
  }

  private getExperimentProductIds(exp: Partial<ExperimentVM>): Set<string> {
    const ids = new Set<string>();

    // Singular: each experiment has one experimentProduct
    const single = (exp as any)?.experimentProduct;
    if (single) {
      const id = String(single?.id ?? single?.productId ?? single?.id_product ?? '').trim();
      if (id) ids.add(id);
    }

    // Fallback: array forms (future-proof)
    const rawProducts =
      (exp as any)?.experimentProducts ?? (exp as any)?.experiment_products ?? (exp as any)?.products ?? [];
    for (const p of rawProducts || []) {
      const id = String(p?.id ?? p?.productId ?? p?.id_product ?? '').trim();
      if (id) ids.add(id);
    }

    return ids;
  }

  private getTagKey(tag: vm_tag | null | undefined): string {
    return String((tag as any)?.id ?? (tag as any)?.id_tag ?? tag?.tagTitle ?? '')
      .trim()
      .toLowerCase();
  }

  private getOrCreateSelectionSet(map: Map<string, Set<string>>, project: ProjectVM): Set<string> {
    const projectId = String(project?.id_project ?? '').trim();
    if (!map.has(projectId)) {
      map.set(projectId, new Set<string>());
    }
    return map.get(projectId)!;
  }

  private toggleInSelectionMap(map: Map<string, Set<string>>, project: ProjectVM, value: string): void {
    const normalized = String(value ?? '').trim();
    if (!normalized) return;
    const set = this.getOrCreateSelectionSet(map, project);
    if (set.has(normalized)) set.delete(normalized);
    else set.add(normalized);
    this.cdr.detectChanges();
  }

  private cleanupSelectionMap(map: Map<string, Set<string>>, validProjectIds: Set<string>): void {
    for (const key of Array.from(map.keys())) {
      if (!validProjectIds.has(key)) {
        map.delete(key);
      }
    }
  }

  onProjectEditNote(project: ProjectVM): void {
    this.editProject(project);
  }

  private getProjectStartDateMs(project: ProjectVM): number | null {
    const d = project?.project_start_date ? new Date(project.project_start_date).getTime() : NaN;
    return Number.isFinite(d) ? d : null;
  }

  private readonly projectSortComparators: Record<ProjectSortKey, (a: ProjectVM, b: ProjectVM) => number> = {
    modifiedDate: (a, b) => {
      const da = a.projectModifiedDate ? new Date(a.projectModifiedDate).getTime() : -Infinity;
      const db = b.projectModifiedDate ? new Date(b.projectModifiedDate).getTime() : -Infinity;
      return db - da; // latest first
    },
    startDateAsc: (a, b) => {
      const da = this.getProjectStartDateMs(a);
      const db = this.getProjectStartDateMs(b);
      if (da === null && db === null) return 0;
      if (da === null) return 1; // nulls last
      if (db === null) return -1; // nulls last
      return da - db; // oldest -> newest
    },
    startDateDesc: (a, b) => {
      const da = this.getProjectStartDateMs(a);
      const db = this.getProjectStartDateMs(b);
      if (da === null && db === null) return 0;
      if (da === null) return 1; // nulls last
      if (db === null) return -1; // nulls last
      return db - da; // newest -> oldest
    },
  };

  private focusPendingProjectOrTop(): void {
    requestAnimationFrame(() => {
      const projectId = this.pendingProjectIdToFocus;
      this.pendingProjectIdToFocus = null;

      const container = this.projectsPageBody?.nativeElement;
      if (!container) {
        this.scrollPageBodyToTop();
        return;
      }

      if (projectId) {
        const row = document.getElementById(`prj-row-${projectId}`) as HTMLElement | null;
        if (row) {
          const headerEl = container.querySelector('.mat-mdc-header-row') as HTMLElement | null;
          const headerHeight = headerEl?.getBoundingClientRect().height ?? 0;
          const extraGap = 8;

          const containerRect = container.getBoundingClientRect();
          const rowRect = row.getBoundingClientRect();

          const targetTop = container.scrollTop + (rowRect.top - containerRect.top) - headerHeight - extraGap;

          container.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
          return;
        }
      }
      this.scrollPageBodyToTop();
    });
  }

  private scrollPageBodyToTop(): void {
    this.projectsPageBody?.nativeElement?.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
